import { inject, singleton } from 'tsyringe';
import type { IRGSTransport, TransportResponse } from 'atemoya-contracts';
import { RestService } from './RestService';
import { WebSocketService } from './WebSocketService';
import { ITransportService, TransportRequestTypes } from './ITransportService';

export enum TransportType {
    WEBSOCKET = 'websocket',
    REST = 'rest',
}

export interface TransportConfig {
    type: TransportType;
    url: string;
    timeout?: number;
}

@singleton()
export class TransportService implements IRGSTransport {
    private _adapter: ITransportService;
    private sessionId: string | undefined;
    private transportType: TransportType;

    constructor(
        @inject('TRANSPORT_TYPE') type: TransportType = TransportType.WEBSOCKET,
        @inject(RestService) restService: RestService,
        @inject(WebSocketService) wsService: WebSocketService
    ) {
        this.transportType = type;

        if (type === TransportType.REST) {
            console.log('[TransportService] Using REST transport');
            this._adapter = restService;
        } else {
            console.log('[TransportService] Using WebSocket transport');
            this._adapter = wsService;
        }
    }

    public async sendInit(payload: Record<string, any> = {}): Promise<TransportResponse> {
        const response = await this.send(TransportRequestTypes.INIT, payload);
        this.sessionId = response.identity.sessionId as string;
        return response;
    }

    public async sendAction(payload: Record<string, any> = {}): Promise<TransportResponse> {
        if (!this.sessionId) {
            throw new Error('[TransportService] sendAction called before sendInit');
        }
        return this.send(TransportRequestTypes.ACTION, { ...payload, sessionId: this.sessionId });
    }

    private async send(
        type: TransportRequestTypes,
        payload: Record<string, any> = {}
    ): Promise<TransportResponse> {
        const clientRequestId = this.getUUID();
        console.log(`Transport Request::: ${type}: ${JSON.stringify(payload)}, clientRequestId: ${clientRequestId}`);
        const response: TransportResponse = await this._adapter.send(
            type,
            payload,
            clientRequestId
        );
        console.log(`Transport Response::: ${response.requestType}: ${JSON.stringify(response)}`);

        return response;
    }

    public async connect(): Promise<void> {
        await this._adapter.connect?.();
    }

    public disconnect() {
        this._adapter.disconnect();
    }

    public getTransportType(): TransportType {
        return this.transportType;
    }

    private getUUID(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        // iOS Safari < 15.4 does not implement crypto.randomUUID().
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
            const random = (Math.random() * 16) | 0;
            const value = char === 'x' ? random : (random & 0x3) | 0x8;
            return value.toString(16);
        });
    }
}
